import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/ui/use-toast";
import {
  AssetDimensionPayload,
  AssetFailurePayload,
  AssetTypePayload,
  createAssetClass,
  createAssetDimension,
  createAssetFailure,
  createAssetType,
  deleteAssetClass,
  deleteDimension,
  getAllAssetTypes,
  getAssetClassList,
  getAssetFailureList,
  getAssetTypeList,
  getDimensionList,
  getTypeDimensionList,
  searchAssetClassList,
  updateAssetClass,
  updateAssetDimension,
  updateAssetFailure,
  updateAssetType,
} from "./api";

// Asset Class Hooks
export const useAssetClassList = (showInactive: boolean = true) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetClassList(),
    () => getAssetClassList(showInactive)
  );
};

export const useCreateAssetClass = () => {
  return useSmartMutation((payload: any) => createAssetClass(payload), {
    onSuccess: () => {
      toast({
        title: "Asset class created successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetClassList()
        .slice(0, 4);
      const searchKey = QueryKeyFactory.module.ax.settings
        .assetSearchClassList("")
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: key,
      });

      queryClient.invalidateQueries({
        queryKey: searchKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to create asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAssetClass = () => {
  return useSmartMutation((payload: any) => updateAssetClass(payload), {
    onSuccess: () => {
      toast({
        title: "Asset class updated successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings.assetClassList();
      queryClient.invalidateQueries({
        queryKey: key,
      });

      queryClient.refetchQueries({
        queryKey: key,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to update asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

// Asset Type Hooks
export const useAssetTypeList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetTypeList(code),
    () => getAssetTypeList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useAllAssetTypes = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.ax.settings.allAssetTypes()],
    () => getAllAssetTypes()
  );
};

export const useCreateAssetType = () => {
  return useSmartMutation<any, Error, AssetTypePayload>(
    (payload: AssetTypePayload) => createAssetType(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset type created successfully!",
          variant: "default",
        });

        const assetTypeKey = QueryKeyFactory.module.ax.settings
          .assetTypeList()
          .slice(0, 4);
        const typeDimension = QueryKeyFactory.module.ax.settings
          .assetTypeDimensionList()
          .slice(0, 4);
        const assetClassKey = QueryKeyFactory.module.ax.settings
          .assetClassList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: assetTypeKey,
        });

        queryClient.invalidateQueries({
          queryKey: typeDimension,
        });

        queryClient.invalidateQueries({
          queryKey: assetClassKey,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create asset type: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateAssetType = () => {
  return useSmartMutation<any, Error, AssetTypePayload>(
    (payload: AssetTypePayload) => updateAssetType(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset type updated successfully!",
          variant: "default",
        });

        const assetTypeKey = QueryKeyFactory.module.ax.settings
          .assetTypeList()
          .slice(0, 3);
        const typeDimension = QueryKeyFactory.module.ax.settings
          .assetTypeDimensionList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: assetTypeKey,
        });

        queryClient.invalidateQueries({
          queryKey: typeDimension,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update asset type: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// Asset Dimension Hooks
export const useTypeDimensionList = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetTypeDimensionList(queryParams),
    () => getTypeDimensionList(queryParams)
  );
};

export const useCreateAssetDimension = () => {
  return useSmartMutation((payload: any) => createAssetDimension(payload), {
    onSuccess: () => {
      toast({
        title: "Asset dimension created successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetDimensionList()
        .slice(0, 4);
      const typeListKey = QueryKeyFactory.module.ax.settings
        .assetTypeDimensionList()
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: key,
      });
      queryClient.invalidateQueries({
        queryKey: typeListKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to create asset dimension: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAssetDimension = () => {
  return useSmartMutation<any, Error, AssetDimensionPayload>(
    (payload: AssetDimensionPayload) => updateAssetDimension(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset dimension updated successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.settings
          .assetDimensionList()
          .slice(0, 4);
        const typeListKey = QueryKeyFactory.module.ax.settings
          .assetTypeDimensionList()
          .slice(0, 4);
        queryClient.invalidateQueries({
          queryKey: key,
        });
        queryClient.invalidateQueries({
          queryKey: typeListKey,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update asset dimension: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// Asset Failure Mode Hooks
export const useAssetFailureList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetFailureList(code),
    () => getAssetFailureList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useCreateAssetFailure = () => {
  return useSmartMutation<any, Error, AssetFailurePayload>(
    (payload: AssetFailurePayload) => createAssetFailure(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset failure mode created successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.settings
          .assetFailureList()
          .slice(0, 4);
        const typeListKey = QueryKeyFactory.module.ax.settings
          .assetTypeList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: key,
        });

        queryClient.invalidateQueries({
          queryKey: typeListKey,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create asset failure mode: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateAssetFailure = () => {
  return useSmartMutation<any, Error, AssetFailurePayload>(
    (payload: AssetFailurePayload) => updateAssetFailure(payload),
    {
      onSuccess: () => {
        toast({
          title: "Asset failure mode updated successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.settings
          .assetFailureList()
          .slice(0, 4);
        queryClient.invalidateQueries({
          queryKey: key,
        });

        queryClient.refetchQueries({
          queryKey: key,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create asset failure mode: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useDeleteAssetClass = () => {
  return useSmartMutation((code: string) => deleteAssetClass(code), {
    onSuccess: () => {
      toast({
        title: "Asset class deleted successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetClassList()
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: key,
      });

      queryClient.refetchQueries({
        queryKey: key,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to update asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteAssetType = () => {
  return useSmartMutation((code: string) => deleteAssetClass(code), {
    onSuccess: () => {
      toast({
        title: "Asset type deleted successfully!",
        variant: "default",
      });

      const assetTypeKey = QueryKeyFactory.module.ax.settings
        .assetTypeList()
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: assetTypeKey,
      });

      queryClient.refetchQueries({
        queryKey: assetTypeKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to update asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteFailureMode = () => {
  return useSmartMutation((code: string) => deleteAssetClass(code), {
    onSuccess: () => {
      toast({
        title: "Failure mode deleted successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetFailureList()
        .slice(0, 4);
      const typeListKey = QueryKeyFactory.module.ax.settings
        .assetTypeList()
        .slice(0, 4);

      queryClient.invalidateQueries({
        queryKey: key,
      });

      queryClient.invalidateQueries({
        queryKey: typeListKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to update asset class: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useDimensionList = (code: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetDimensionList(code),
    () => getDimensionList(code),
    {
      enabled: Boolean(code),
    }
  );
};

export const useSearchAssetClassList = (searchData: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.settings.assetSearchClassList(searchData),
    () => searchAssetClassList(searchData)
  );
};

export const useDeleteDimension = () => {
  return useSmartMutation((code: string) => deleteDimension(code), {
    onSuccess: () => {
      toast({
        title: "Dimension deleted successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.settings
        .assetDimensionList()
        .slice(0, 4);
      const typeListKey = QueryKeyFactory.module.ax.settings
        .assetTypeDimensionList()
        .slice(0, 4);
      queryClient.invalidateQueries({
        queryKey: key,
      });
      queryClient.invalidateQueries({
        queryKey: typeListKey,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to delete dimension: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};
